import { useMemo, useState } from "react";
import CalculatorShell, { Field, Result, inputClass } from "@/components/CalculatorShell";
import { getCalculator } from "@/data/calculators";
import { proRataSalary, partMonthPay, gbp, longDate, currentMinimumWage } from "@/lib/employment-calcs";

const calc = getCalculator("pro-rata-salary-calculator");

const NMW = currentMinimumWage();

const METHODS = [
  { id: "default", label: "Nothing, or I'm not sure" },
  { id: "calendar", label: "Calendar days in the month" },
  { id: "working", label: "Working days in the month" },
];

const pct = (n) => `${(n * 100).toLocaleString("en-GB", { maximumFractionDigits: 1 })}%`;

export default function ProRataSalaryCalculator() {
  const [form, setForm] = useState({ fullTimeSalary: "", fullTimeHours: "37.5", partTimeHours: "", ageBand: "21plus" });
  const [month, setMonth] = useState({ firstDay: "", lastDay: "", method: "default" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setM = (k) => (e) => setMonth((f) => ({ ...f, [k]: e.target.value }));

  const ready = form.fullTimeSalary && form.fullTimeHours && form.partTimeHours;
  const r = useMemo(() => (ready ? proRataSalary(form) : null), [form, ready]);
  const annual = r && !r.error ? r.annual : Number(form.fullTimeSalary) || 0;
  const m = useMemo(
    () => (annual > 0 && month.firstDay && month.lastDay ? partMonthPay({ annualSalary: annual, ...month }) : null),
    [annual, month]
  );

  return (
    <CalculatorShell
      calc={calc}
      howItWorks={
        <>
          <p>
            Part-time workers must be paid at least the same rate as a comparable full-time worker, for the proportion of
            hours they work. This is the "pro rata principle" in the Part-time Workers Regulations 2000 (reg 1(2) and reg 5).
          </p>
          <p>
            <strong>Pro-rata salary = full-time salary × part-time hours ÷ full-time hours.</strong> Monthly pay is the
            annual figure divided by 12, and weekly pay is the annual figure divided by 52.
          </p>
          <p>
            For a <strong>part month</strong>, use the method the contract or an incorporated pay policy sets out. Where
            it says nothing, salary is treated as building up day by day (Apportionment Act 1870, s2), so each calendar day
            is worth 1/365 of the annual salary, or 1/366 in a leap year (<em>Hartley v King Edward VI College</em> [2017]
            UKSC 39).
          </p>
          <p>
            The hourly figure is checked against the minimum wage rates from {longDate(NMW.from)}.
            It is a guide only: minimum wage law has its own rules for working out a salaried worker's hours.
          </p>
        </>
      }
      example={
        <p>
          A full-time role pays £30,000 for 37.5 hours a week. Alex works 22.5 hours, which is 60%, so their salary is{" "}
          <strong>£18,000</strong>, or £1,500 a month. That is £15.38 an hour, comfortably above the £12.71 National Living Wage.
        </p>
      }
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Full-time salary (£ a year)" htmlFor="fte">
          <input id="fte" type="number" min="0" step="1" inputMode="decimal" className={inputClass} value={form.fullTimeSalary} onChange={set("fullTimeSalary")} data-testid="pr-fte" />
        </Field>
        <Field label="Full-time hours a week" htmlFor="fth">
          <input id="fth" type="number" min="0" step="0.25" inputMode="decimal" className={inputClass} value={form.fullTimeHours} onChange={set("fullTimeHours")} data-testid="pr-fth" />
        </Field>
        <Field label="Part-time hours a week" htmlFor="pth">
          <input id="pth" type="number" min="0" step="0.25" inputMode="decimal" className={inputClass} value={form.partTimeHours} onChange={set("partTimeHours")} data-testid="pr-pth" />
        </Field>
        <Field label="Employee's age, for the minimum wage check" htmlFor="age">
          <select id="age" className={inputClass} value={form.ageBand} onChange={set("ageBand")} data-testid="pr-age">
            {NMW.bands.map((b) => (
              <option key={b.id} value={b.id}>{b.label}</option>
            ))}
          </select>
        </Field>
      </div>

      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result
          items={[
            { label: "Pro-rata salary", value: gbp(r.annual), big: true, sub: `${pct(r.fraction)} of full time` },
            { label: "A month", value: gbp(r.monthly) },
            { label: "A week", value: gbp(r.weekly) },
            { label: "An hour (approx.)", value: gbp(r.hourly), sub: `minimum ${gbp(r.minimumRate)}` },
          ]}
          notes={[
            ...(r.belowMinimum ? [`Warning: about ${gbp(r.hourly)} an hour is below the ${gbp(r.minimumRate)} minimum (${r.minimumLabel}). Check this before you offer it.`] : []),
            ...(r.moreThanFullTime ? ["The part-time hours are more than the full-time hours. Check the figures."] : []),
            "Pro-rate holiday, bonus and other benefits in the same way, unless there is an objective reason not to.",
          ]}
        />
      )}

      <div className="mt-10 border-t border-ink/15 pt-8">
        <p className="font-display text-lg font-semibold">Joining or leaving mid-month?</p>
        <p className="mt-1 text-sm text-ink/60">Uses the pro-rata salary above, or the full-time salary if you have not entered part-time hours.</p>
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <Field label="First day paid this month" htmlFor="mfirst">
            <input id="mfirst" type="date" className={inputClass} value={month.firstDay} onChange={setM("firstDay")} data-testid="pr-first" />
          </Field>
          <Field label="Last day paid this month" htmlFor="mlast">
            <input id="mlast" type="date" className={inputClass} value={month.lastDay} onChange={setM("lastDay")} data-testid="pr-last" />
          </Field>
          <Field label="What does the contract or pay policy say about part months?" htmlFor="mmethod">
            <select id="mmethod" className={inputClass} value={month.method} onChange={setM("method")} data-testid="pr-method">
              {METHODS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </Field>
        </div>
        {m?.error && <p className="mt-6 text-sm font-semibold text-signal">{m.error}</p>}
        {m && !m.error && (
          <Result
            testId="pr-part-month"
            items={[
              {
                label: "Pay for the part month",
                value: gbp(m.amount),
                big: true,
                sub: m.method === "default"
                  ? `${m.counted} days × 1/${m.outOf} of ${gbp(annual)}`
                  : `${m.counted} of ${m.outOf} ${m.method === "working" ? "working days (Monday to Friday)" : "days"} × ${gbp(m.monthly)}`,
              },
            ]}
            notes={[
              m.method === "default"
                ? "Where the contract is silent, this is the legal default. If you have always used another method, check whether it has become an implied term before relying on this figure."
                : "This follows the method you said the contract sets out. If the contract does not actually say so, the default is 1/365 of the annual salary per calendar day.",
            ]}
          />
        )}
      </div>
    </CalculatorShell>
  );
}
